import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../store/AuthContext";

const API_URL = process.env.REACT_APP_API_URL;

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { login } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Invalid credentials");
      }

      // Save to context & local storage
      login(data.user, data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      localStorage.setItem("token", data.token);

      // Redirect after login
      const redirect = localStorage.getItem("redirectAfterLogin");
      if (redirect) {
        localStorage.removeItem("redirectAfterLogin");
        navigate(redirect, { replace: true });
      } else {
        if (data.user.role === "teacher") {
          navigate("/teacher/dashboard");
        } else if (data.user.role === "student") {
          navigate("/student/dashboard");
        } else {
          navigate("/unauthorized");
        }
      }
    } catch (err) {
      setError(err.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-blue-600 to-purple-700 px-6 py-12">
      <form
        onSubmit={handleLogin}
        className="bg-white rounded-xl shadow-2xl p-10 max-w-md w-full"
        aria-label="Login form"
      >
        <h2 className="text-3xl font-extrabold mb-8 text-center text-gray-900">
          Login to Your Account
        </h2>

        {error && (
          <div
            role="alert"
            className="mb-6 text-center text-red-700 bg-red-100 rounded-md py-2 px-4 font-medium"
          >
            {error}
          </div>
        )}

        <label htmlFor="email" className="block mb-2 font-semibold">
          Email
        </label>
        <input
          id="email"
          type="email"
          className="w-full p-3 border rounded-lg mb-6"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
        />

        <label htmlFor="password" className="block mb-2 font-semibold">
          Password
        </label>
        <div className="relative mb-6">
          <input
            id="password"
            type={showPassword ? "text" : "password"}
            className="w-full p-3 border rounded-lg pr-12"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
          />
          <button
            type="button"
            onClick={() => setShowPassword((prev) => !prev)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? "Hide" : "Show"}
          </button>
        </div>

        <div className="text-right text-sm mb-4">
          <a href="/forgot-password" className="text-blue-700 hover:underline">
            Forgot Password?
          </a>
        </div>

        <button
          type="submit"
          disabled={loading}
          className={`w-full bg-gradient-to-r from-blue-600 to-purple-700 text-white font-bold py-3 rounded-lg shadow-lg transition duration-300 ${
            loading ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"
          }`}
        >
          {loading ? "Logging in..." : "Login"}
        </button>

        <p className="mt-6 text-center text-sm">
          Don’t have an account?{" "}
          <a href="/register" className="text-blue-700 hover:underline font-medium">
            Register
          </a>
        </p>
      </form>
    </div>
  );
}
